// React
import React, { useMemo } from 'react';

// utils
import PropTypes from 'prop-types';
import withStyles from '@material-ui/core/styles/withStyles';
import classNames from 'classnames';
import isEmpty from 'lodash/isEmpty';
import { keyEmptyCheck } from 'aurora-utils/utils';

// Components
import Paper from '@material-ui/core/Paper';
import Chip from '@material-ui/core/Chip';
import Button from '@material-ui/core/Button';
import Hidden from '@material-ui/core/Hidden';
import Typography from '@material-ui/core/Typography';
import ADGMImage from '../ADGMImage';
import ADGMAlert from '../ADGMAlert';

// Styles
import styles from './styles';

function ADGMPanel(props) {
  const {
    classes,
    children,
    id,
    order,
    title,
    mandatory,
    isTwoLayout,
    hasPadding,
    infoPanel,
    removeBorder,
    extraHeaderComponents,
    headerChip,
    subtitle,
    hasBodyBorder,
    isEditable,
    handleEdit,
    handleRightButton,
    handleRightTitle,
    handleDelete,
    customClasses,
  } = props;
  const informationPanel = useMemo(() => {
    if (!isEmpty(infoPanel))
      return (
        <ADGMAlert type={infoPanel?.type} visible className={classes.infoPanel}>
          {keyEmptyCheck(infoPanel?.text)}
        </ADGMAlert>
      );
    return null;
  }, [infoPanel]);

  const ChipClassesMap = {
    success: classes.greenChip,
    error: classes.redChip,
  };

  return (
    <>
      {infoPanel?.position === 'top' && informationPanel}
      <Paper
        key={id}
        variant='outlined'
        className={
          removeBorder
            ? classNames(classes.borderBottomNone, classes.borderRadiusNone)
            : classNames(classes.panelContainer, customClasses?.container)
        }
        style={{ order }}
      >
        <div
          className={
            removeBorder
              ? classNames(classes.titleHead, classes.borderRadiusNone)
              : classNames(classes.titleHead, customClasses?.titleHead)
          }
        >
          <Typography variant='h6' component='h1' className={classNames(classes.title, customClasses?.title)}>
            {keyEmptyCheck(title)}
            {mandatory && <span className={classes.requiredStar}>*</span>}
          </Typography>
          {isEditable && (
            <Hidden xsDown>
              <div className={classes.titleHeadActions}>
                {typeof handleEdit === 'function' ? (
                  <>
                    <Button onClick={handleEdit} startIcon={<ADGMImage name='PencilRed.svg' alt='edit-pencil' />}>
                      <Typography>{keyEmptyCheck('Edit')}</Typography>
                    </Button>
                    <span>
                      <ADGMImage name='DividerRed.svg' alt='divider' />
                    </span>
                  </>
                ) : (
                  ''
                )}

                <Button onClick={handleDelete} startIcon={<ADGMImage name='DustbinRed.svg' alt='delete-dustbin' />}>
                  <Typography>{keyEmptyCheck('delete')}</Typography>
                </Button>
              </div>
            </Hidden>
          )}
          {!isEmpty(extraHeaderComponents) && extraHeaderComponents}
          {!isEmpty(headerChip) && (
            <Chip
              label={headerChip?.label}
              className={classNames(ChipClassesMap[headerChip?.type], classes.chipbadges)}
            />
          )}
          {subtitle && subtitle}
          {handleRightTitle !== null && (
            <Button className={classes.backButton} onClick={handleRightButton}>
              <Typography>{handleRightTitle}</Typography>
            </Button>
          )}
        </div>
        <div
          className={classNames({
            [classes.panelGridContainer]: isTwoLayout,
            [classes.containerPadding]: hasPadding,
            [classes.containerBorder]: hasBodyBorder,
          })}
        >
          {children}
        </div>
      </Paper>
      {infoPanel?.position === 'bottom' && informationPanel}
    </>
  );
}

ADGMPanel.propTypes = {
  classes: PropTypes.object.isRequired,
  children: PropTypes.node.isRequired,
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  order: PropTypes.number,
  title: PropTypes.string.isRequired,
  mandatory: PropTypes.bool,
  isTwoLayout: PropTypes.bool,
  hasPadding: PropTypes.bool,
  infoPanel: PropTypes.object,
  extraHeaderComponents: PropTypes.node,
  headerChip: PropTypes.object,
  removeBorder: PropTypes.bool,
  subtitle: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
  hasBodyBorder: PropTypes.bool,
  isEditable: PropTypes.bool,
  handleEdit: PropTypes.func,
  handleDelete: PropTypes.func,
  handleRightButton: PropTypes.func,
  handleRightTitle: PropTypes.string,
  customClasses: PropTypes.object,
};

ADGMPanel.defaultProps = {
  id: null,
  order: null,
  name: null,
  mandatory: false,
  isTwoLayout: false,
  hasPadding: false,
  count: null,
  infoPanel: null,
  extraHeaderComponents: null,
  headerChip: null,
  removeBorder: false,
  subtitle: null,
  defaultExpanded: true,
  onExpand: undefined,
  isEditable: false,
  hasBodyBorder: false,
  handleRightTitle: null,
  handleEdit: () => {},
  handleDelete: () => {},
  customClasses: {},
  handleRightButton: () => {},
};

export default withStyles(styles)(ADGMPanel);
